import { groupCitySpecs } from "./generatedCityRouteConfigs";

const domain = "https://www.cheapcabgrab.com";

const makeCcgRoute = ([slug, city, postcode, roads, time, hubs, access], direction) => {
  const fromAirport = direction === "from-airport";
  const routeName = fromAirport ? `Heathrow to ${city}` : `${city} to Heathrow`;
  const reverseName = fromAirport ? `${city} to Heathrow` : `Heathrow to ${city}`;
  const path = `/${fromAirport ? `heathrow-to-${slug}` : `${slug}-to-heathrow`}`;
  const reversePath = `/${fromAirport ? `${slug}-to-heathrow` : `heathrow-to-${slug}`}`;
  // These paid-search landing pages require customers to choose both locations.
  const emptyLocations = fromAirport && ["southampton", "manchester", "birmingham", "oxford"].includes(slug);
  const pickup = fromAirport ? "Heathrow Airport" : `${city}, ${postcode}`;
  const dropoff = fromAirport ? `${city}, ${postcode}` : "Heathrow Airport";

  return {
    id: path.slice(1),
    path,
    liveStripVariant: fromAirport ? "heathrow-city" : "heathrow",
    seo: {
      routeName: `${routeName} taxi`,
      title: `${routeName} Taxi | Fixed-Price Airport Transfer | CheapCabGrab`,
      description: `Book a fixed-price ${routeName} taxi via ${roads}. Licensed drivers, secure online booking and live Heathrow checks for ${hubs}.`,
      keywords: `${routeName} taxi, ${city} Heathrow airport transfer, Heathrow taxi ${postcode}, fixed price taxi ${city}`,
      canonical: `${domain}${path}`,
      ogTitle: `${routeName} Taxi - Fixed-Price Airport Transfer`,
      ogDescription: `Reliable ${routeName} taxis with fixed upfront fares, licensed drivers and live Heathrow journey checks.`,
      serviceName: `CheapCabGrab - ${routeName} Taxi`,
      areaServed: ["Heathrow Airport", city, postcode],
      offerCatalogName: `${routeName} Taxi Transfers`,
      offerName: `${routeName} fixed-price taxi`,
      offerDescription: `Fixed-price taxi transfers between Heathrow Airport and ${city}, including ${hubs}.`,
      reservationName: `Taxi booking for ${routeName}`,
    },
    hero: {
      eyebrow: `${routeName} taxi route`,
      h1: `${routeName} Taxi - Fixed-Price Airport Transfers`,
      subtitle: fromAirport
        ? `Heathrow terminals 2, 3, 4 and 5 to ${city} homes, hotels, stations and venues.`
        : `${city} and ${postcode} pickups for Heathrow terminals 2, 3, 4 and 5.`,
      body: fromAirport
        ? `Book a licensed taxi from Heathrow to ${city} with flight tracking, 60 minutes of included airport waiting and a fixed upfront fare.`
        : `Book a licensed taxi from ${city} to Heathrow with pickup planning, terminal checks and a fixed upfront fare with no surge pricing.`,
    },
    quote: {
      label: "Get instant quotes",
      note: "Free - Fast - No obligation",
      presetPickup: emptyLocations ? "" : pickup,
      presetDropoff: emptyLocations ? "" : dropoff,
      pickupPlaceholder: fromAirport ? "Heathrow terminal or pickup point" : `Enter ${city} pickup postcode or address`,
      dropoffPlaceholder: fromAirport ? `Enter ${city} destination` : "Heathrow Airport or terminal",
    },
    flags: [
      "Fixed upfront fares - no meters or surge pricing",
      fromAirport ? "Flight tracking and 60 minutes airport waiting" : "Terminal-aware pickup and check-in planning",
      `Main route checks: ${roads}`,
      "Saloon, estate, MPV and group vehicle options",
    ],
    reviewPageKey: path,
    reviewAriaLabel: `${routeName} customer reviews`,
    lhrStatusTitle: `Live Heathrow checks for ${routeName} taxis`,
    floatingCta: `Book ${routeName} Taxi`,
    mobileCta: `Book ${routeName} Taxi`,
    supportNudge: `Need ${routeName} help?`,
    generatedContent: {
      overviewTitle: `${routeName} at a glance`,
      overviewIntro: `A direct airport taxi route planned around ${roads}, the correct Heathrow terminal and the exact ${city} address.`,
      overviewCards: [
        {
          title: "Journey basics",
          items: [
            `Typical journey time: ${time}`,
            `Main corridor: ${roads}`,
            `City postcode reference: ${postcode}`,
            "Final time depends on traffic, terminal and loading",
          ],
        },
        {
          title: `${city} coverage`,
          body: `Common journey points include ${hubs}. Enter the full address for an accurate door-to-door quote.`,
        },
        {
          title: "Why book the route together?",
          items: [
            "One confirmed pickup and destination",
            "Fixed fare shown before booking",
            "Licensed driver and secure confirmation",
            "Support for luggage, child seats and accessibility notes",
          ],
        },
      ],
      planningTitle: `${routeName} journey planning`,
      planningIntro: `Practical details that can affect pickup timing, vehicle access and the final Heathrow transfer plan.`,
      planningCards: [
        {
          title: fromAirport ? "Heathrow pickup planning" : `${city} pickup planning`,
          body: fromAirport
            ? "Provide the flight number and terminal so the driver can monitor the arrival and follow the agreed airport meeting plan."
            : `Provide the exact ${city} address, passenger-ready time and flight departure details so the Heathrow arrival target can be planned.`,
        },
        {
          title: `${city} access context`,
          body: `Allow for ${access}. Share hotel, station, venue or restricted-access instructions before travel.`,
        },
        {
          title: "Passengers and luggage",
          body: "Select a vehicle for the complete passenger and luggage total, including pushchairs, wheelchairs, sports equipment or oversized cases.",
        },
      ],
      links: [
        { title: `Travel ${reverseName}`, body: "Open the paired direction page with the pickup and drop-off defaults reversed.", to: reversePath, label: `${reverseName} taxi` },
        { title: "Check your Heathrow terminal", body: "Confirm the correct terminal before booking or travelling.", to: "/terminalFinder", label: "Open Terminal Finder" },
        { title: "Travelling as a group?", body: "Compare larger vehicle options when passengers and luggage need more space.", to: "/minibus", label: "View minibus options" },
      ],
      faqTitle: `${routeName} taxi - FAQs`,
    },
    faqItems: [
      { question: `How long does ${routeName} take by taxi?`, answer: `A typical journey takes around ${time}, but ${roads} traffic, the Heathrow terminal, stops and loading time can change this.` },
      { question: `Can you serve hotels and stations in ${city}?`, answer: `Yes. CheapCabGrab covers ${hubs}. Enter the exact pickup or destination to check the route and fare.` },
      { question: "Do you cover every Heathrow terminal?", answer: "Yes. Book Terminal 2, 3, 4 or 5 and add the flight details so the airport part of the journey can be planned correctly." },
      { question: "Is the taxi fare fixed before travel?", answer: "Yes. The confirmed booking shows the agreed fare before travel, based on the route, vehicle and journey details supplied." },
      { question: "Can I book an MPV or larger vehicle?", answer: "Yes. Add the full passenger and luggage totals when requesting a quote so suitable saloon, estate, MPV or group options can be shown." },
    ],
  };
};

export const generatedCcgRouteConfigs = {};
export const generatedCcgRouteRoutes = [];

groupCitySpecs.forEach((citySpec) => {
  ["from-airport", "to-airport"].forEach((direction) => {
    const route = makeCcgRoute(citySpec, direction);
    generatedCcgRouteConfigs[route.id] = route;
    generatedCcgRouteRoutes.push({ path: route.path, routeKey: route.id });
  });
});

export default generatedCcgRouteConfigs;
